<template>
  <div
    class="flex w-full bg-white h-screen overflow-y-auto items-start make-items-center"
  >
    <div
      class="w-full sm:w-full lg:w-1/2 items-center flex justify-center p-8 md:p-16 h-full select-none"
    >
      <div
        class="max-w-md mx-auto w-full space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700"
      >
        <div
          class="flex flex-col sm:flex-row items-center justify-center md:justify-between"
        >
          <img :src="logo.url" class="h-8 w-auto" v-if="logo" />
          <span
            class="border-transparent h-4 w-0 border-r sm:h-10 sm:border-brand-ash mx-3"
          ></span>

          <span
            class="text-base md:text-xl font-semibold text-black tracking-tight"
          >
            Check Your Inbox
          </span>
        </div>

        <div class="w-full space-y-6">
          <div
            class="bg-gray-50 border border-gray-100 rounded-2xl p-8 text-center space-y-6 shadow-sm relative overflow-hidden"
          >
            <div
              class="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-brand-primary opacity-5 rounded-full blur-2xl"
            ></div>

            <div class="flex justify-center">
              <div
                class="w-16 h-16 bg-brand-primary bg-opacity-10 rounded-full flex items-center justify-center text-brand-primary"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            <div class="space-y-2">
              <p
                class="text-brand-secondary text-sm md:text-base leading-relaxed"
              >
                We've sent a magic link to <br />
                <span class="font-semibold text-black text-lg">{{
                  email
                }}</span>
              </p>

              <div class="flex flex-col items-center gap-2 py-2">
                <span
                  class="text-xs font-semibold text-brand-body text-opacity-60 uppercase tracking-widest"
                  >Link Expires In</span
                >
                <div
                  class="text-3xl font-mono font-bold text-brand-primary bg-white px-4 py-2 rounded-xl shadow-inner border border-gray-50"
                >
                  {{ formattedTime }}
                </div>
              </div>

              <p class="text-brand-primary text-sm font-medium">
                Click the link in the email to sign in instantly.
              </p>
              <p class="text-brand-primary text-sm font-medium">
                Don't see it? <b>Please check your spam folder.</b>
              </p>
            </div>
          </div>

          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2">
              <transition name="fade" mode="out-in">
                <div
                  v-if="resendCountdown > 0"
                  class="text-center py-3 px-6 rounded-xl border border-dashed border-brand-ash border-opacity-50 bg-gray-50 bg-opacity-50"
                >
                  <p
                    class="text-sm text-brand-body text-opacity-60 font-medium"
                  >
                    Resend available in
                    <span class="text-brand-primary font-bold"
                      >{{ resendCountdown }}s</span
                    >
                  </p>
                </div>
                <Button
                  v-else
                  title="Resend Magic Link"
                  type="button"
                  :loader="user.magicLinkLoader"
                  @click="resendLink"
                  class="w-full !bg-brand-primary !bg-opacity-5 !text-brand-primary border border-brand-primary border-opacity-20 hover:!bg-opacity-10 transition-all duration-300 font-semibold"
                />
              </transition>
            </div>

            <router-link
              :to="{
                name: 'Login',
                params: $route.params,
                query: $route.query,
              }"
              class="w-full"
            >
              <Button
                icon="left-arrow"
                title="Login with Password"
                type="button"
                class="w-full !bg-white !text-brand-body border border-brand-ash hover:border-brand-primary transition-colors font-medium"
              />
            </router-link>
          </div>

          <div
            class="pt-8 border-t border-brand-ash border-opacity-40 text-center space-y-4"
          >
            <p class="text-brand-body text-sm text-opacity-70">
              New to Medusa?
              <router-link
                to="/join-us"
                class="font-semibold text-brand-primary hover:underline"
                >Join us</router-link
              >
            </p>

            <p class="text-brand-body text-xs text-opacity-50 italic">
              Need help? Chat with us using the button below.
            </p>
          </div>
        </div>
      </div>
    </div>

    <InfoColumn />
  </div>
</template>

<script>
import Auth from "@/modules/Auth";
import InfoColumn from "../common/InfoColumn";
import { onMounted, onBeforeUnmount, inject, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios1 from "axios";

export default {
  components: { InfoColumn },
  name: "EmailSent",
  setup() {
    const { user, loginWithLink } = Auth();
    const axios = inject("axios");
    const route = useRoute();
    const router = useRouter();
    const poller = ref(null);
    const timer = ref(null);

    const email = computed(() => route.params.email || route.query.email || "");
    const publicId = ref(route.query.public_id || "");
    const expiresAt = ref(Number(route.query.expires_at || 0));

    // Countdown logic
    const countdown = ref(0);
    const resendCountdown = ref(30);

    const updateCountdown = () => {
      if (!expiresAt.value) return;
      const now = Math.floor(Date.now() / 1000);
      const remaining = expiresAt.value - now;
      countdown.value = Math.max(0, remaining);

      if (resendCountdown.value > 0) {
        resendCountdown.value--;
      }

      if (countdown.value <= 0) {
        handleTimeout();
      }
    };

    const formattedTime = computed(() => {
      const minutes = Math.floor(countdown.value / 60);
      const seconds = countdown.value % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0"
      )}`;
    });

    const stopPolling = () => {
      if (poller.value) {
        clearInterval(poller.value);
        poller.value = null;
      }
    };

    const stopTimer = () => {
      if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
      }
    };

    const handleTimeout = () => {
      stopPolling();
      stopTimer();
      router.push({
        name: "ExpiredMagicLink",
        query: { email: email.value },
      });
    };

    const startPolling = () => {
      stopPolling();
      poller.value = setInterval(async () => {
        if (!expiresAt.value || Date.now() > expiresAt.value * 1000) {
          handleTimeout();
          return;
        }

        if (localStorage.getItem(`login_success_${publicId.value}`)) {
          stopPolling();
          localStorage.removeItem(`login_success_${publicId.value}`);
          window.location.href = "/home";
          return;
        }

        if (!publicId.value) return;

        try {
          const res = await axios.api.get(`/auth/status/${publicId.value}`);
          if (!res?.data?.status) return;

          const payload = res.data.data;
          if (payload.status === "verified" && payload.exchange_token) {
            stopPolling();
            stopTimer();

            const ex = await axios.api.post("/auth/exchange", {
              exchange_token: payload.exchange_token,
            });
            if (ex?.data?.status) {
              const data = ex.data.data;

              localStorage.setItem("LoggedUser", data.token);
              localStorage.setItem("IsloggIn", true);
              localStorage.setItem("username", data.user.name);
              localStorage.setItem("email", data.user.email);
              localStorage.setItem("company_id", data.user.company_id || "");
              localStorage.setItem("user_hash", data.user.user_hash || "");
              localStorage.setItem("last_name", data.user.last_name || "");
              localStorage.setItem("user_id", data.user.id || "");
              localStorage.setItem("last_login", data.user.last_login_at || "");

              if (data.distributor === 1)
                localStorage.setItem("distributor", true);

              window.location.href = "/home";
            } else {
              router.replace({
                name: "ExpiredMagicLink",
                query: { type: "system" },
              });
            }
          } else if (payload.status === "expired") {
            handleTimeout();
          }
        } catch (e) {
          console.error("Polling error:", e);
        }
      }, 3000);
    };

    const resendLink = () => {
      loginWithLink(email.value);
      resendCountdown.value = 30; // Reset countdown after resending
    };

    onMounted(() => {
      if (publicId.value && expiresAt.value) {
        startPolling();
        updateCountdown();
        timer.value = setInterval(updateCountdown, 1000);
      }

      window.addEventListener("storage", onStorage);
    });

    const onStorage = (event) => {
      if (event.key === `login_success_${publicId.value}`) {
        stopPolling();
        stopTimer();
        localStorage.removeItem(`login_success_${publicId.value}`);
        window.location.href = "/home";
      }
    };

    watch(
      () => [route.query.public_id, route.query.expires_at],
      ([newPublicId, newExpiresAt]) => {
        publicId.value = newPublicId || "";
        expiresAt.value = Number(newExpiresAt || 0);

        // reset UI countdowns
        resendCountdown.value = 30;

        // restart timers/polling with new expiry
        stopPolling();
        stopTimer();

        if (publicId.value && expiresAt.value) {
          startPolling();
          updateCountdown();
          timer.value = setInterval(updateCountdown, 1000);
        }
      },
      { immediate: false }
    );

    const logo = ref("");
    onMounted(() => {
      axios1.get(process.env.BASE_URL_API + "/logo/get").then((res) => {
        logo.value = res.data;
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("storage", onStorage);
      stopPolling();
      stopTimer();
    });

    return {
      user,
      email,
      logo,
      countdown,
      resendCountdown,
      formattedTime,
      resendLink,
    };
  },
};
</script>

<style scoped>
.shadow-inner {
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
